import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2 } from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AudioPlayer = ({ currentTrack, playlist = [], onTrackChange, onTrackEnd, onClose, onChangeReciter, onTimeUpdate }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(70);
  const audioRef = useRef(null);
  
  // Get full audio URL - convert /uploads/ to /api/files/
  const getAudioUrl = (audioUrl) => {
    if (!audioUrl) return '';
    if (audioUrl.startsWith('http')) return audioUrl;
    // Convert /uploads/audio/ to /api/files/audio/
    const convertedUrl = audioUrl.replace('/uploads/', '/api/files/');
    return `${BACKEND_URL}${convertedUrl}`;
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlayPause = async () => {
    if (audioRef.current && currentTrack?.audioUrl) {
      try {
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          await audioRef.current.play();
          setIsPlaying(true);
        }
      } catch (error) {
        console.error('Audio playback error:', error);
        setIsPlaying(false);
      }
    }
  };

  const handlePrevious = () => {
    if (playlist.length > 0 && onTrackChange) {
      const currentIndex = playlist.findIndex(t => t.surahId === currentTrack?.surahId || t.id === currentTrack?.id);
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : playlist.length - 1;
      onTrackChange(playlist[prevIndex]);
    }
  };

  const handleNext = () => {
    if (playlist.length > 0 && onTrackChange) {
      const currentIndex = playlist.findIndex(t => t.surahId === currentTrack?.surahId || t.id === currentTrack?.id);
      const nextIndex = currentIndex < playlist.length - 1 ? currentIndex + 1 : 0;
      onTrackChange(playlist[nextIndex]);
    }
  };

  const handleEnded = () => {
    // Auto play next track
    if (playlist.length > 0 && onTrackChange) {
      const currentIndex = playlist.findIndex(t => t.surahId === currentTrack?.surahId || t.id === currentTrack?.id);
      if (currentIndex < playlist.length - 1) {
        // Play next track
        onTrackChange(playlist[currentIndex + 1]);
      } else {
        // End of playlist
        setIsPlaying(false);
        if (onTrackEnd) {
          onTrackEnd();
        }
      }
    } else {
      setIsPlaying(false);
    }
  };

  const handleSeek = (value) => {
    const newTime = (value[0] / 100) * duration;
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  const handleVolumeChange = (value) => {
    setVolume(value[0]);
    if (audioRef.current) {
      audioRef.current.volume = value[0] / 100;
    }
  };

  // Only reload when audioUrl actually changes
  const prevAudioUrlRef = React.useRef(null);
  const isFirstLoadRef = React.useRef(true);
  
  useEffect(() => {
    const newAudioUrl = currentTrack?.audioUrl;
    
    // Only load if audioUrl is different from previous
    if (newAudioUrl && newAudioUrl !== prevAudioUrlRef.current) {
      console.log('🔄 Audio URL changed from', prevAudioUrlRef.current, 'to', newAudioUrl);
      prevAudioUrlRef.current = newAudioUrl;
      
      if (audioRef.current) {
        setCurrentTime(0);
        audioRef.current.load();
        console.log('📀 Audio loaded for:', currentTrack);

        // Auto-play
        const tryAutoPlay = async () => {
          try {
            await new Promise(resolve => setTimeout(resolve, 300));
            if (audioRef.current && audioRef.current.readyState >= 2) {
              console.log('▶️ Auto-playing...');
              await audioRef.current.play();
              setIsPlaying(true);
              console.log('✅ Playing');
            }
          } catch (error) {
            console.log('❌ Autoplay failed:', error);
            setIsPlaying(false);
          }
        };
        tryAutoPlay();
      }
      isFirstLoadRef.current = false;
    } else if (newAudioUrl && !isFirstLoadRef.current) {
      console.log('✅ Audio URL unchanged - CONTINUING PLAYBACK WITHOUT RELOAD');
      // Do nothing - let the audio continue playing
    }
  }, [currentTrack?.audioUrl]);

  // Don't render if no valid audio source
  if (!currentTrack?.audioUrl) {
    return null;
  }

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url.replace('/uploads/', '/api/files/')}`;
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-[#F5EFE7] border-t-2 border-[#D5C4A1] shadow-lg z-50">
      <div className="max-w-7xl mx-auto px-8 py-4">
        {/* Current Track Info with Reciter */}
        {currentTrack && (
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              {currentTrack.reciterImage && (
                <img
                  src={getImageUrl(currentTrack.reciterImage)}
                  alt={currentTrack.reciterName}
                  className="w-10 h-10 rounded-full object-cover"
                />
              )}
              <div>
                <p className="text-sm text-[#5C4033] font-semibold">
                  {currentTrack.surahName || currentTrack.name} {currentTrack.surahName ? 'Suresi' : ''}
                </p>
                {currentTrack.reciterName && (
                  <p className="text-xs text-[#8B7355]">
                    {currentTrack.reciterName}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {onChangeReciter && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    console.log('Hafız Değiştir clicked!');
                    if (onChangeReciter) {
                      onChangeReciter();
                    }
                  }}
                  className="text-[#8B7355] hover:bg-[#E8DCC8]"
                >
                  Hafız Değiştir
                </Button>
              )}
              {onClose && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="text-[#8B7355] hover:bg-[#E8DCC8]"
                >
                  Kapat
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Progress Bar */}
        <div className="mb-4">
          <Slider
            value={[duration > 0 ? (currentTime / duration) * 100 : 0]}
            onValueChange={handleSeek}
            max={100}
            step={0.1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-[#8B7355] mt-1">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center space-x-6">
          {/* Previous Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handlePrevious}
            className="text-[#6B5744] hover:bg-[#E8DCC8]"
            disabled={!playlist || playlist.length === 0}
          >
            <SkipBack className="h-6 w-6" />
          </Button>

          {/* Play/Pause Button */}
          <Button
            variant="default"
            size="icon"
            onClick={togglePlayPause}
            className="bg-[#8B7355] hover:bg-[#6B5744] text-white h-12 w-12 rounded-full"
          >
            {isPlaying ? (
              <Pause className="h-6 w-6" />
            ) : (
              <Play className="h-6 w-6" />
            )}
          </Button>

          {/* Next Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleNext}
            className="text-[#6B5744] hover:bg-[#E8DCC8]"
            disabled={!playlist || playlist.length === 0}
          >
            <SkipForward className="h-6 w-6" />
          </Button>

          {/* Volume Control */}
          <div className="flex items-center space-x-2 ml-8">
            <Volume2 className="h-5 w-5 text-[#8B7355]" />
            <div className="w-24">
              <Slider
                value={[volume]}
                onValueChange={handleVolumeChange}
                max={100}
                step={1}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        src={getAudioUrl(currentTrack?.audioUrl)}
        preload="auto"
        onTimeUpdate={(e) => {
          const time = e.target.currentTime;
          setCurrentTime(time);
          // Call parent's onTimeUpdate callback if provided
          if (onTimeUpdate) {
            onTimeUpdate(time);
          }
        }}
        onLoadedMetadata={(e) => {
          setDuration(e.target.duration);
          console.log('Audio loaded, duration:', e.target.duration);
        }}
        onEnded={handleEnded}
        onError={(e) => {
          console.error('Audio error:', e.target.error);
          setIsPlaying(false);
        }}
        onCanPlay={() => {
          console.log('Audio can play');
        }}
      />
    </div>
  );
};

export default AudioPlayer;